// deadlines_panel.jsx
import React, { useState, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { fetchDoc } from "../services/api";
import MiniDeadlinesCalendar from "./mini_deadlines_calendar";
import MiniDeadlinesList from "./mini_deadlines_list";
import EditDocumentModal from "./edit_document_modal";
import EditContractModal from "./edit_contract_modal";

export default function DeadlinesPanel({
  docs = [],
  contracts = [],
  signedDocs = [],
  loading = false,
  token,
  showCalendar = true,
  showList = true,
  currentMonth,
  onMonthChange,
  withNavigation = false,
  onDocUpdated,
  onSignedDocUpdated,
  onContractUpdated,
}) {
  const [viewDoc, setViewDoc] = useState(null);
  const [viewContract, setViewContract] = useState(null);
  const [openDocModal, setOpenDocModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("document");
  const [loadingDoc, setLoadingDoc] = useState(false);

  // normalizzo il mese (se non arriva, uso quello corrente)
  const month = useMemo(
    () => (currentMonth ? dayjs(currentMonth).startOf("month") : dayjs().startOf("month")),
    [currentMonth]
  );

  const safeDocs = useMemo(() => {
    return Array.isArray(docs) ? docs : [];
  }, [docs]);

  const safeContracts = useMemo(() => {
    return Array.isArray(contracts) ? contracts : [];
  }, [contracts]);

  const safeSignedDocs = useMemo(() => {
    return Array.isArray(signedDocs) ? signedDocs : [];
  }, [signedDocs]);

  const handlePrevMonth = () => {
    if (!onMonthChange) return;
    onMonthChange(month.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    if (!onMonthChange) return;
    onMonthChange(month.add(1, "month"));
  };

  const getDocPreviewLink = (doc) => {
    if (!doc) return null;
    return doc.download_link || doc.file?.url || null;
  };

  const openItem = useCallback(
    async ({ kind, entity }) => {
      // CONTRATTI
      if (kind === "contract") {
        const c = entity || {};
        const empId = c.employee?.id ?? c.employee_id ?? null;
        setViewContract({ ...c, employeeId: empId });
        return;
      }

      // DOCUMENTI CON FIRMA
      if (kind === "signed-document") {
        const signedDoc = entity || {};

        if (!signedDoc.id) {
          Swal.fire({
            icon: "error",
            title: "Documento non trovato",
            text: "Impossibile recuperare il documento con firma.",
          });
          return;
        }

        setSelectedDocType("signed_document");
        setViewDoc(signedDoc);
        setOpenDocModal(true);
        return;
      }

      // DOCUMENTI ORDINARI
      const d = entity || {};
      const id = d.id;

      if (!id) {
        Swal.fire({
          icon: "error",
          title: "Dati mancanti",
          text: "Id documento non presente.",
        });
        return;
      }

      setLoadingDoc(true);
      try {
        const full = await fetchDoc(token, id, "payrolls");
        const merged = {
          ...d,
          ...(full || {}),
          file: full?.file ?? d?.file ?? null,
          download_link: getDocPreviewLink(full) || getDocPreviewLink(d) || null,
        };

        setSelectedDocType("document");
        setViewDoc(merged);
        setOpenDocModal(true);
      } catch (err) {
        const msg = err?.response?.data
          ? Object.values(err.response.data).flat().join(" ")
          : err?.message || "Errore caricamento documento.";

        Swal.fire({
          icon: "error",
          title: "Impossibile aprire il documento",
          text: msg,
        });
      } finally {
        setLoadingDoc(false);
      }
    },
    [token]
  );

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs text-gray-500 z-10">
          Caricamento scadenze…
        </div>
      )}

      <div className={loading ? "pointer-events-none opacity-60" : ""}>
        {/* intestazione mese + frecce */}
        {withNavigation && (
          <div className="flex items-center justify-between mb-0 bg-indigo-200 rounded-t-md">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="text-sm px-2 my-0.5 rounded-md ml-1 hover:bg-gray-50 bg-indigo-50"
            >
              ‹
            </button>
            <div className="text-xs font-semibold tracking-wide text-indigo-800 uppercase">
              {month.format("MMMM YYYY")}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="text-sm px-2 my-0.5 rounded-md mr-1 hover:bg-gray-50 bg-indigo-50"
            >
              ›
            </button>
          </div>
        )}

        {showCalendar && (
          <MiniDeadlinesCalendar
            month={month}
            docDeadlines={safeDocs.map((d) => d.due_date || d.expiry_date || d.date)}
            contractDeadlines={safeContracts.map((c) => c.due_date || c.end_date)}
            signedDocDeadlines={safeSignedDocs.map((d) => d.due_date)}
          />
        )}

        {showList && (
          <div className="mt-3 hidden sm:block">
            <MiniDeadlinesList
              month={month}
              docs={safeDocs}
              contracts={safeContracts}
              signedDocs={safeSignedDocs}
              onItemClick={openItem}
            />
          </div>
        )}
      </div>


      {viewDoc && (
        <EditDocumentModal
          open={openDocModal}
          onClose={() => {
            setOpenDocModal(false);
            setViewDoc(null);
          }}
          onReopen={() => setOpenDocModal(true)}
          doc={viewDoc}
          documentType={selectedDocType}
          mode="view"
          allowInlineEditToggle
          showPreviewModal={selectedDocType !== "signed_document"}
          onSaved={(updated) => {
            if (selectedDocType === "signed_document") {
              onSignedDocUpdated?.(updated);
            } else {
              onDocUpdated?.(updated);
            }

            setOpenDocModal(false);
            setViewDoc(null);
          }}
        />
      )}

      {viewContract && (
        <EditContractModal
          open={!!viewContract}
          onClose={() => setViewContract(null)}
          contract={viewContract}
          onSaved={(updated) => {
            onContractUpdated?.(updated);
            setViewContract(null);
          }}
        />
      )}

      {/* overlay quando loadingDoc */}
      {loadingDoc && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-indigo-700 z-20">
          Caricamento documento…
        </div>
      )}
    </div>
  );
}